import { api } from "@/lib/api"

export interface RentRecord {
  id: string
  lease_id: string
  amount: number
  currency: string
  due_date: string
  paid_date?: string | null
  status: "pending" | "paid" | "partial" | "late" | "unpaid" | "cancelled"
  notes?: string | null
  lease?: {
    property?: { name?: string }
    tenant?: { full_name?: string }
  }
}

export interface Expense {
  id: string
  property_id?: string | null
  category: string
  amount: number
  currency: string
  date: string
  description?: string | null
  notes?: string | null
  property?: { name?: string }
}

export interface ManualEarning {
  id: string
  user_id: string
  property_id?: string | null
  amount: number
  currency: string
  date: string
  description?: string | null
  category: string
  property?: { name?: string }
}

export interface DashboardStats {
  total_properties: number
  occupied_properties: number
  available_properties: number
  total_tenants: number
  active_leases: number
  monthly_income: number
  monthly_expenses: number
  open_maintenance_issues: number
  rent_collected: number
  rent_expected: number
  historical_cashflow: {
    name: string
    revenue: number
    expenses: number
  }[]
  currency: string
}

export const financesService = {
  async listRentRecords(lease_id?: string): Promise<RentRecord[]> {
    const params = lease_id ? { lease_id } : {}
    const { data } = await api.get("/api/rent-records", { params })
    return data.data
  },
  async getRentRecord(id: string): Promise<RentRecord> {
    const { data } = await api.get(`/api/rent-records/${id}`)
    return data.data
  },
  async createRentRecord(input: Record<string, unknown>): Promise<RentRecord> {
    const { data } = await api.post("/api/rent-records", input)
    return data.data
  },
  async updateRentRecord(id: string, input: Record<string, unknown>): Promise<RentRecord> {
    const { data } = await api.put(`/api/rent-records/${id}`, input)
    return data.data
  },
  async deleteRentRecord(id: string): Promise<void> {
    await api.delete(`/api/rent-records/${id}`)
  },

  async listExpenses(property_id?: string): Promise<Expense[]> {
    const params = property_id ? { property_id } : {}
    const { data } = await api.get("/api/expenses", { params })
    return data.data
  },
  async createExpense(input: Record<string, unknown>): Promise<Expense> {
    const { data } = await api.post("/api/expenses", input)
    return data.data
  },
  async updateExpense(id: string, input: Record<string, unknown>): Promise<Expense> {
    const { data } = await api.put(`/api/expenses/${id}`, input)
    return data.data
  },
  async deleteExpense(id: string): Promise<void> {
    await api.delete(`/api/expenses/${id}`)
  },

  async getDashboardStats(): Promise<DashboardStats> {
    const { data } = await api.get("/api/dashboard/stats")
    return data.data
  },

  async listManualEarnings(): Promise<ManualEarning[]> {
    const { data } = await api.get("/api/manual-earnings")
    return data.data
  },
  async createManualEarning(input: Record<string, unknown>): Promise<ManualEarning> {
    const { data } = await api.post("/api/manual-earnings", input)
    return data.data
  },
  async updateManualEarning(id: string, input: Record<string, unknown>): Promise<ManualEarning> {
    const { data } = await api.put(`/api/manual-earnings/${id}`, input)
    return data.data
  },
  async deleteManualEarning(id: string): Promise<void> {
    await api.delete(`/api/manual-earnings/${id}`)
  },
}
